import { Orders } from '@database/types';
import { checkFulfillmentCanFileClaim, checkFulfillmentItemCanFileClaim } from '@helpers';
import { CREDIT_BONUS_TYPES, ClaimCategory } from '@pulse/constants/claims';
import { ClaimModel } from '@pulse/models';
import OrderService from '@pulse/services/OrderService';
import dayjs from 'dayjs';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { useClaimStore } from '~/store/claim';

export const useOrdersStore = defineStore('conciergeOrders', () => {
  const currentOrder = ref(null);
  const claim = useClaimStore();
  const { currentShipmentId, claimCategory } = storeToRefs(claim);
  const service = new OrderService(`${useRuntimeConfig().public.apiBaseUrl}`);
  const selectedItems = ref([]);
  const itemEligibility = ref({});
  const orderItems = computed(() => currentOrder.value.order.orderItems || []);

  async function find(email, orderNumber, storeId) {
    currentOrder.value = await service.find(email, orderNumber, storeId);

    claim.setCustomer({
      customerName: currentOrder.value.customerName,
      email: email.trim(),
      sourceOrderId: orderNumber.trim(),
    });

    if (currentOrder.value?.systemOrderId) {
      await claim.findClaimSession(currentOrder.value.systemOrderId);
    }

    itemEligibility.value = currentOrder.value.eligibility || {};

    return currentOrder.value;
  }

  function reset() {
    currentOrder.value = null;
  }

  async function fetchOrder(email, sourceOrderId) {
    const dbOrder = await find(email, sourceOrderId);

    if (!dbOrder) return;

    currentOrder.value = dbOrder;
  }

  function setOrderDetails(details) {
    currentOrder.value = { ...currentOrder.value, ...details };
  }

  /**
   * Availability straight from the platform (ENG-6074). The lookup already attached
   * availability from our twice-daily cache, which has been both stale and wrongly derived; this is
   * the answer to prefer when the two disagree. Only `inStock` is replaced — the count stays off this
   * surface entirely.
   *
   * Merges onto the existing items rather than replacing them, and leaves them untouched when the
   * call comes back empty — an unreachable platform should not look like an out-of-stock catalog.
   */
  async function loadLiveInventory() {
    const storeId = currentOrder.value?.order?.storeId;
    const sourceOrderId = currentOrder.value?.orderId;
    if (!storeId || !sourceOrderId) return;

    const lines = await service.getLiveOrderInventory(storeId, sourceOrderId);
    if (!lines.length) return;

    // Keyed on the line id first: it is unique per order line, where one variant can appear on two
    // lines of the same order and collapse them into a single entry. Variant is the fallback for a
    // platform that reports no line id.
    //
    // Product id is the last resort, and shares `byItem` because it is the same field: bundle
    // components arrive from a different source that puts their product id in `id`, so their line
    // never matches on either of the first two and a variant-less one matched nothing at all.
    //
    // Null-ish keys are dropped rather than stringified — `String(undefined)` is a real string, so
    // two items that both lack a variant id would otherwise match each other.
    const key = value => (value == null ? null : String(value));
    const indexBy = get => new Map(lines.map(line => [get(line), line]).filter(([id]) => id !== null));

    const byItem = indexBy(line => key(line.id));
    const byVariant = indexBy(line => key(line.variantId));

    currentOrder.value = {
      ...currentOrder.value,
      orderItems: (currentOrder.value.orderItems ?? []).map(item => {
        const live =
          byItem.get(key(item.sourceItemId)) ??
          byVariant.get(key(item.sourceVariantId)) ??
          byItem.get(key(item.sourceProductId));
        if (!live) return item;

        return { ...item, inStock: live.inStock };
      }),
    };
  }

  async function getProductChildren(sourceProductId) {
    const storeId = currentOrder.value?.order?.storeId;
    if (!storeId || !sourceProductId) return [];

    return service.getProductChildren(storeId, sourceProductId);
  }

  function getClaimedQuantity(sourceItemId) {
    const claimItems = shippingClaims.value
      .flatMap(claim => claim.claimItems)
      .filter(claimItem => claimItem.sourceItemId === sourceItemId);

    return claimItems.reduce((acc, claimItem) => acc + claimItem.quantity, 0);
  }

  const claimFeedClaims = computed(() => {
    if (!currentOrder.value.existingClaims) {
      return [];
    }

    return ClaimModel.fromArrayExtending(currentOrder.value?.existingClaims, {
      order: currentOrder.value,
    });
  });

  const formattedCreditBonus = computed(() => {
    if (currentOrder.value.settings.credit.bonus === null) return null;

    return currentOrder.value.settings.credit.bonusType === CREDIT_BONUS_TYPES.fixed
      ? new Intl.NumberFormat(navigator.language, {
          currency: currentOrder.value.currency || 'USD',
          style: 'currency',
        }).format(currentOrder.value.settings.credit.bonus / currentOrder.value.exchangeRate)
      : `${currentOrder.value.settings.credit.bonus}%`;
  });

  const soonestFileDate = computed(() => {
    const fulfillments = currentOrder.value?.fulfillments;
    if (!fulfillments) return null;

    const [date] = fulfillments.map(({ eligibleAt }) => eligibleAt).sort();

    return date;
  });

  const latestFileDate = computed(() => {
    return currentOrder.value.fulfillments
      .map(({ uneligibleAt }) => uneligibleAt)
      .sort()
      .at(-1);
  });

  const eligibleFulfillments = computed(() => {
    return currentOrder.value.fulfillments.filter(({ eligible }) => eligible);
  });

  const tooSoonToFile = computed(() => {
    if (currentOrder.value.fulfillments.length === 0) {
      return false;
    }

    return currentOrder.value.fulfillments.every(
      ({ eligibilityCode, eligible }) => !eligible && eligibilityCode !== 'expired',
    );
  });

  /**
   * TODO - add logic
   */
  const showTooLateAlert = computed(() => {
    const order = currentOrder?.value;
    if (order?.protection?.warranty || !order?.protection?.shipping) return false;

    return outsideShippingWindow.value;
  });

  const outsideShippingWindow = computed(() => {
    return (
      currentOrder?.value.fulfillments[0].eligible === false &&
      currentOrder?.value.fulfillments[0].eligibilityCode === 'expired'
    );
  });
  const eligibleForClaim = computed(() => {
    if (!currentOrder.value) return false;

    return currentOrder.value.protected || currentOrder?.value?.protection?.warranty || returnsEnabled.value;
  });

  const eligibleForShippingClaim = computed(() => {
    if (!currentOrder.value) return false;

    return currentOrder.value?.protection?.shipping;
  });

  const eligibleForWarrantyClaim = computed(() => {
    if (!currentOrder.value) return false;

    return currentOrder.value?.protection?.warranty;
  });

  const allCurrentOrderItems = computed(() => {
    if (!currentOrder.value) return [];

    return currentOrder.value.fulfillments.reduce((all, fulfillment) => {
      return all.concat(
        fulfillment.items.map(({ item, quantity }) => ({
          ...item,
          currency: currentOrder.value.currency || 'USD',
          quantity,
          sourceFulfillmentId: fulfillment.sourceFulfillmentId,
        })),
      );
    }, []);
  });

  const deliveredFulfillments = computed(() => {
    if (!currentOrder.value) return [];

    return currentOrder.value.fulfillments.filter(({ fulfillmentStatus }) => fulfillmentStatus === 'DELIVERED');
  });

  const hasDeliveredFulfillments = computed(() => {
    return deliveredFulfillments.value.length > 0;
  });

  const currentShipmentOrderItems = computed(() => {
    if (currentOrder?.value?.fulfillments?.length < 1 || !currentShipmentId?.value) {
      return [];
    }

    return currentOrder.value.fulfillments
      .find(({ sourceFulfillmentId }) => sourceFulfillmentId === currentShipmentId.value)
      .items.map(({ item, quantity, sourceFulfillmentId }) => ({
        ...item,
        currency: currentOrder.value.currency || 'USD',
        quantity,
        sourceFulfillmentId,
      }));
  });

  const originalAddress = computed(() => {
    return currentOrder.value?.shippingAddress;
  });

  const setOriginalAddress = address => {
    currentOrder.value.shippingAddress = address;
  };

  const orderHasShipped = computed(() => {
    return currentOrder.value.fulfillments.length > 0;
  });

  const isOta = computed(() => {
    return currentOrder.value.isOneTimeAppeasement;
  });

  const returnsEnabled = computed(() => {
    const caps = currentOrder.value?.settings?.capabilities;

    return !!caps?.returns;
  });

  const supportsReturns = computed(() => {
    return currentOrder.value && returnsEnabled.value;
  });

  const supportsWarranties = computed(() => {
    return currentOrder.value && !!currentOrder.value.settings?.capabilities?.warranties;
  });

  const returns = computed(() => {
    return currentOrder.value?.returns ?? [];
  });

  const claims = computed(() => {
    return currentOrder?.value?.existingClaims ?? [];
  });

  /** In-store orders are minted by the claim itself, so they never carry fulfillments. */
  const isInStoreOrder = computed(() => {
    return currentOrder.value?.order?.salesChannel === Orders.SalesChannel.IN_STORE;
  });

  const latestClaimId = computed(() => claims.value[0]?.id ?? null);

  const shippingClaims = computed(() => {
    return currentOrder?.value?.existingClaims.filter(c => c.claimCategory === ClaimCategory.SHIPPING) ?? [];
  });

  const hasEligibleItems = computed(() => {
    return Object.values(itemEligibility.value).some(value => value.shipping > 0);
  });

  const hasEligibleWarrantyItems = computed(() => {
    return Object.values(itemEligibility.value).some(value => value.warranty.qty > 0);
  });

  // Return eligibility is tracked separately from shipping so that a Closed/Denied
  // shipping or warranty claim frees the item back up for a return (see orders-svc
  // editEligibleClaimsQuantity) without reopening new shipping/warranty claims.
  const hasEligibleReturnItems = computed(() => {
    return Object.values(itemEligibility.value).some(value => value.returns > 0);
  });

  const returnsUrl = computed(() => {
    return currentOrder.value.settings.returns.portalUrl;
  });

  const trackingUrl = computed(() => {
    return currentOrder.value?.settings?.returns?.trackingUrl;
  });

  const canFileClaim = computed(() => {
    if (!eligibleForClaim.value || !eligibleForShippingClaim.value) return false;

    const canFileClaimOnAtleastOneFulfillment = eligibleFulfillments.value.some(fulfillment => {
      return checkFulfillmentCanFileClaim(fulfillment, claims.value);
    });

    return canFileClaimOnAtleastOneFulfillment;
  });

  const capabilities = computed(() => {
    return currentOrder.value.settings?.capabilities;
  });

  function getWarrantyItems(filterFn) {
    return orderItems.value.flatMap(orderItem => {
      const eligibilityEntry = itemEligibility.value[orderItem.sourceItemId];

      if (!eligibilityEntry) return [];

      const { expires, validity, qty } = eligibilityEntry.warranty || {};

      if (!expires || !filterFn(dayjs(expires), qty)) return [];

      //prices and quantites are hidden for warranty items in concierge
      return [
        {
          ...orderItem,
          expires,
          price: undefined,
          quantity: undefined,
          validity,
        },
      ];
    });
  }

  const eligibleWarrantyItems = computed(() => getWarrantyItems((expires, qty) => expires.isAfter(dayjs()) && qty > 0));

  const ineligibleWarrantyItems = computed(() =>
    getWarrantyItems((expires, qty) => expires.isBefore(dayjs()) || qty <= 0),
  );

  const eligibleShippingItems = computed(() => {
    return currentShipmentOrderItems.value.filter(item => {
      return (
        checkFulfillmentItemCanFileClaim(shippingClaims.value, item.sourceItemId, item.quantity) &&
        itemEligibility.value[item.sourceItemId] &&
        itemEligibility.value[item.sourceItemId].shipping > 0
      );
    });
  });

  const ineligibleShippingItems = computed(() => {
    return currentShipmentOrderItems.value.filter(item =>
      eligibleItems.value.every(eItem => eItem.sourceItemId !== item.sourceItemId),
    );
  });

  const ineligibleItems = computed(() => {
    return claimCategory.value === ClaimCategory.WARRANTY
      ? ineligibleWarrantyItems.value
      : ineligibleShippingItems.value;
  });

  const eligibleItems = computed(() => {
    return claimCategory.value === ClaimCategory.WARRANTY ? eligibleWarrantyItems.value : eligibleShippingItems.value;
  });

  const claimSubtypes = computed(() => {
    return currentOrder.value?.claimSubtypes || [];
  });

  const coverageRules = computed(() => {
    return currentOrder.value?.coverageRules || [];
  });

  const warrantyPolicyTypes = computed(() => {
    return currentOrder.value?.warrantyPolicyTypes || [];
  });

  return {
    allCurrentOrderItems,
    canFileClaim,
    capabilities,
    claimFeedClaims,
    claimSubtypes,
    claims,
    coverageRules,
    currentOrder,
    currentShipmentOrderItems,
    deliveredFulfillments,
    eligibleForClaim,
    eligibleForShippingClaim,
    eligibleForWarrantyClaim,
    eligibleFulfillments,
    eligibleItems,
    eligibleShippingItems,
    eligibleWarrantyItems,
    fetchOrder,
    find,
    formattedCreditBonus,
    getClaimedQuantity,
    getProductChildren,
    hasDeliveredFulfillments,
    hasEligibleItems,
    hasEligibleReturnItems,
    hasEligibleWarrantyItems,
    ineligibleItems,
    ineligibleShippingItems,
    ineligibleWarrantyItems,
    isInStoreOrder,
    isOta,
    itemEligibility,
    latestClaimId,
    latestFileDate,
    loadLiveInventory,
    orderHasShipped,
    originalAddress,
    outsideShippingWindow,
    reset,
    returns,
    returnsEnabled,
    returnsUrl,
    selectedItems,
    setOrderDetails,
    setOriginalAddress,
    shippingClaims,
    showTooLateAlert,
    soonestFileDate,
    supportsReturns,
    supportsWarranties,
    tooSoonToFile,
    trackingUrl,
    warrantyPolicyTypes,
  };
});
